import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";
import Paginator from "../../../components/Paginator";
import ApiService from "../../../services/ApiService";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { formatDate } from "../../../utils/helper";
import { exportUsers } from "../../../utils/exportHelper";
import EditUserModal from "./EditUserModal";
import ChangePasswordModal from "./ChangePasswordModal";
import "./List.css";

const List = () => {
  const navigate = useNavigate();
  const [isLoader, setIsLoader] = useState(false);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0,
    new_this_month: 0,
    total_change: 0,
    active_change: 0,
    inactive_change: 0,
    new_change: 0,
    email_users: 0,
    google_users: 0,
    apple_users: 0,
    guest_users: 0
  });
  const [pagination, setPagination] = useState({});
  const [currency] = useState(process.env.REACT_APP_CURRENCY || 'AED');
  const [isExporting, setIsExporting] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [passwordUser, setPasswordUser] = useState(null);

  const [apiData, setApiData] = useState({
    search: "",
    daterange: "",
    page: 1,
    limit: 10,
    sort_by: 'date-desc',
    status: ""
  });

  // Debounce search
  const [searchTimeout, setSearchTimeout] = useState(null);

  useEffect(() => {
    getUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiData.page, apiData.search, apiData.daterange, apiData.sort_by, apiData.status]);

  const getUsers = async () => {
    try {
      setIsLoader(true);
      const params = {
        search: apiData.search,
        page: apiData.page,
        limit: apiData.limit,
        sort_by: apiData.sort_by,
        status: apiData.status
      };
      
      if (apiData.daterange) {
        params.daterange = apiData.daterange;
      }

      const response = await ApiService.request({
        method: "GET",
        url: "admin/users",
        data: params,
      });
      const data = response.data;
      if (data.status) {
        setUsers(data.data);
        setPagination(data.pagination);
        if (data.stats) {
          setStats(data.stats);
        }
      } else {
        toast.error(data.message);
      }
      setIsLoader(false);
    } catch (error) {
      setIsLoader(false);
      console.error('Error fetching users:', error);
    }
  };

  const handlePageChange = (event) => {
    setApiData((prev) => ({ ...prev, page: event }));
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    const timeout = setTimeout(() => {
      setApiData((prev) => ({ ...prev, search: value, page: 1 }));
    }, 300);
    setSearchTimeout(timeout);
  };

  const handleSortChange = (sortBy) => {
    setApiData((prev) => ({ ...prev, sort_by: sortBy, page: 1 }));
  };

  const updateStatus = async (e, id) => {
    e.stopPropagation(); // Prevent row click
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "you want to change its status!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4BC95F",
      confirmButtonText: "Yes",
      cancelButtonColor: "#9E9B9B",
      cancelButtonText: "No",
    });

    if (result.isConfirmed) {
      const response = await ApiService.request({
        method: "POST",
        url: `admin/users/status/${id}`,
      });
      const data = response.data;
      if (data.status) {
        getUsers();
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
    }
  };

  const deleteUser = async (e, user) => {
    e.stopPropagation(); // Prevent row click
    const result = await Swal.fire({
      title: "Delete this account?",
      text: `${user.name || user.email || 'This client'} will be signed out and can no longer log in. Their bookings and membership history are kept.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      confirmButtonText: "Yes, delete",
      cancelButtonColor: "#9E9B9B",
      cancelButtonText: "No",
    });

    if (result.isConfirmed) {
      try {
        const response = await ApiService.request({
          method: "DELETE",
          url: `admin/users/${user._id}`,
        });
        const data = response.data;
        if (data.status) {
          getUsers();
          toast.success(data.message);
        } else {
          toast.error(data.message);
        }
      } catch (error) {
        // ApiService already surfaced the error.
      }
    }
  };

  const openModal = (e, setter, user) => {
    e.stopPropagation(); // Prevent row click
    setter(user);
  };

  const handleRowClick = (userId) => {
    navigate(`/admin/users/${userId}`);
  };

  const formatChange = (change) => {
    const sign = change >= 0 ? '+' : '';
    return `${sign}${change}%`;
  };

  const avatarSrc = (name) =>
    `https://ui-avatars.com/api/?name=${encodeURIComponent(name || 'U')}&background=4F97D8&color=fff&size=128`;

  const handleExport = async (format) => {
    if (isExporting) return;

    try {
      setIsExporting(true);
      
      // Prepare filters from current state
      const filters = {
        search: apiData.search,
        daterange: apiData.daterange,
        status: apiData.status,
        sort_by: apiData.sort_by
      };

      await exportUsers(
        format,
        filters,
        () => {
          // onStart callback
          toast.info(`Exporting users to ${format.toUpperCase()}...`, { autoClose: 2000 });
        },
        () => {
          // onComplete callback
          toast.success(`Users exported to ${format.toUpperCase()} successfully!`, { autoClose: 3000 });
        },
        (error) => {
          // onError callback
          toast.error(`Failed to export users: ${error.message || 'Unknown error'}`, { autoClose: 5000 });
        }
      );
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export users: ${error.message || 'Unknown error'}`, { autoClose: 5000 });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="d-flex flex-column flex-column-fluid">
      <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
        <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
          <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
            <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0 text-capitalize">
              Clients
            </h1>
            <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
              <li className="breadcrumb-item text-muted">
                <Link to="/admin/dashboard" className="text-muted text-hover-primary">Home</Link>
              </li>
              <li className="breadcrumb-item">
                <span className="bullet bg-gray-400 w-5px h-2px"></span>
              </li>
              <li className="breadcrumb-item text-muted">Clients</li>
            </ul>
          </div>
        </div>
      </div>

      <div id="kt_app_content" className="app-content pt-0 flex-column-fluid">
        <div id="kt_app_content_container" className="app-container container-xxl">
          {/* Summary Cards */}
          <div className="px-3 mt-4 mb-4">
            <div className="row g-3">
              <div className="col-md-3 ">
                <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="text-gray-600 fw-semibold mb-0">Total customer</h5>
                    <i className="bi bi-people fs-1 text-gray-400"></i>
                  </div>
                  <h1 className="fw-bold text-dark mb-2">{stats.total?.toLocaleString() || 0}</h1>
                  <p className={`mb-0 ${stats.total_change >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatChange(stats.total_change || 0)} from last month
                  </p>
                </div>
              </div>
              <div className="col-md-3 ">
                <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="text-gray-600 fw-semibold mb-0">Active</h5>
                    <i className="bi bi-person-check fs-1 text-success"></i>
                  </div>
                  <h1 className="fw-bold text-dark mb-2">{stats.active?.toLocaleString() || 0}</h1>
                  <p className={`mb-0 ${stats.active_change >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatChange(stats.active_change || 0)} from last month
                  </p>
                </div>
              </div>
              <div className="col-md-3 ">
                <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="text-gray-600 fw-semibold mb-0">Inactive</h5>
                    <i className="bi bi-person-x fs-1 text-warning"></i>
                  </div>
                  <h1 className="fw-bold text-dark mb-2">{stats.inactive?.toLocaleString() || 0}</h1>
                  <p className={`mb-0 ${stats.inactive_change >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatChange(stats.inactive_change || 0)} from last month
                  </p>
                </div>
              </div>
              <div className="col-md-3 ">
                <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <h5 className="text-gray-600 fw-semibold mb-0">New This Month</h5>
                    <i className="bi bi-person-plus fs-1 text-primary"></i>
                  </div>
                  <h1 className="fw-bold text-dark mb-2">{stats.new_this_month?.toLocaleString() || 0}</h1>
                  <p className={`mb-0 ${stats.new_change >= 0 ? 'text-success' : 'text-danger'}`}>
                    {formatChange(stats.new_change || 0)} from last month
                  </p>
                </div>
              </div>
            </div>

            {/* Sign-in methods */}
            <div className="row g-3 mt-0">
              {[
                { label: 'Email Sign-in', value: stats.email_users, icon: 'bi-envelope', color: 'text-primary' },
                { label: 'Google Sign-in', value: stats.google_users, icon: 'bi-google', color: 'text-danger' },
                { label: 'Apple Sign-in', value: stats.apple_users, icon: 'bi-apple', color: 'text-dark' },
                { label: 'Guest Users', value: stats.guest_users, icon: 'bi-person-dash', color: 'text-gray-500' },
              ].map((card) => (
                <div className="col-md-3" key={card.label}>
                  <div className="bg-white p-4 rounded-4 shadow-sm h-100">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="text-gray-600 fw-semibold mb-0">{card.label}</h5>
                      <i className={`bi ${card.icon} fs-1 ${card.color}`}></i>
                    </div>
                    <h1 className="fw-bold text-dark mb-2">{(card.value || 0).toLocaleString()}</h1>
                    <p className="mb-0 text-muted">
                      {stats.total ? Math.round(((card.value || 0) / stats.total) * 100) : 0}% of all customers
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Control Bar */}
          <div className="users-control-bar bg-white p-4 rounded-4 mb-4 shadow-sm">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="users-control-left">
                <h3 className="users-control-title mb-0 fw-bold">Customer</h3>
              </div>
              <div className="users-control-center flex-grow-1" style={{ maxWidth: '400px' }}>
                <div className="users-search-container position-relative">
                  <i className="bi bi-search users-search-icon position-absolute" style={{ left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#999' }}></i>
                  <input 
                    type="text" 
                    className="users-search-input form-control ps-10"
                    placeholder="Search"
                    defaultValue={apiData.search}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className="users-control-right d-flex gap-3">
                {/* Status Filter */}
                <div className="dropdown">
                    <button 
                        className="btn btn-outline-secondary dropdown-toggle"
                        type="button"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                    >
                        {apiData.status === 'true' ? 'Active' : apiData.status === 'false' ? 'Inactive' : 'All Status'}
                    </button>
                    <ul className="dropdown-menu">
                        <li>
                            <button 
                                className={`dropdown-item ${apiData.status === '' ? 'active' : ''}`}
                                onClick={() => setApiData(prev => ({ ...prev, status: '', page: 1 }))}
                            >
                                All Status
                            </button>
                        </li>
                        <li>
                            <button 
                                className={`dropdown-item ${apiData.status === 'true' ? 'active' : ''}`}
                                onClick={() => setApiData(prev => ({ ...prev, status: 'true', page: 1 }))}
                            >
                                Active
                            </button>
                        </li>
                        <li>
                            <button 
                                className={`dropdown-item ${apiData.status === 'false' ? 'active' : ''}`}
                                onClick={() => setApiData(prev => ({ ...prev, status: 'false', page: 1 }))}
                            >
                                Inactive
                            </button>
                        </li>
                    </ul>
                </div>

                <div className="dropdown">
                  <button 
                    className="btn btn-outline-secondary dropdown-toggle users-export-btn"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                    disabled={isExporting}
                  >
                    {isExporting ? 'Exporting...' : 'Export as'}
                    {/* <i className="bi bi-chevron-down ms-2"></i> */}
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button 
                        className="dropdown-item" 
                        type="button"
                        onClick={() => handleExport('csv')}
                        disabled={isExporting}
                      >
                        <i className="bi bi-filetype-csv me-2"></i>CSV
                      </button>
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        type="button"
                        onClick={() => handleExport('excel')}
                        disabled={isExporting}
                      >
                        <i className="bi bi-file-earmark-excel me-2"></i>Excel
                      </button>
                    </li>
                    <li>
                      <button 
                        className="dropdown-item" 
                        type="button"
                        onClick={() => handleExport('pdf')}
                        disabled={isExporting}
                      >
                        <i className="bi bi-filetype-pdf me-2"></i>PDF
                      </button>
                    </li>
                  </ul>
                </div>
                <div className="dropdown">
                  <button 
                    className="btn btn-outline-secondary dropdown-toggle users-order-btn"
                    type="button"
                    data-bs-toggle="dropdown"
                    aria-expanded="false"
                  >
                    Custom order
                    <i className="bi bi-arrow-up-down ms-2"></i>
                  </button>
                  <ul className="dropdown-menu">
                    <li>
                      <button 
                        className={`dropdown-item ${apiData.sort_by === 'name-asc' ? 'active' : ''}`}
                        type="button"
                        onClick={() => handleSortChange('name-asc')}
                      >
                        Name A-Z
                      </button>
                    </li>
                    <li>
                      <button 
                        className={`dropdown-item ${apiData.sort_by === 'name-desc' ? 'active' : ''}`}
                        type="button"
                        onClick={() => handleSortChange('name-desc')}
                      >
                        Name Z-A
                      </button>
                    </li>
                    <li>
                      <button 
                        className={`dropdown-item ${apiData.sort_by === 'date-desc' ? 'active' : ''}`}
                        type="button"
                        onClick={() => handleSortChange('date-desc')}
                      >
                        Date (Newest)
                      </button>
                    </li>
                    <li>
                      <button 
                        className={`dropdown-item ${apiData.sort_by === 'date-asc' ? 'active' : ''}`}
                        type="button"
                        onClick={() => handleSortChange('date-asc')}
                      >
                        Date (Oldest)
                      </button>
                    </li>
                    <li>
                      <button 
                        className={`dropdown-item ${apiData.sort_by === 'status' ? 'active' : ''}`}
                        type="button"
                        onClick={() => handleSortChange('status')}
                      >
                        Status
                      </button>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>

          {/* Users Table */}
          <div className="card">
            <div className="card-body pt-0">
              <div id="kt_users_table_wrapper" className="dataTables_wrapper dt-bootstrap4 no-footer">
                <div className="table-responsive">
                  <table className="table align-middle table-hover fs-6 gy-5 dataTable no-footer clients-table">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>ID</th>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Bookings</th>
                        <th>Total spent</th>
                        <th>Member since</th>
                        <th>Status</th>
                        <th className="text-end">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600 fs-6">
                      {users.length > 0 ? users.map((user, index) => (
                        <tr 
                          key={user._id || index}
                          style={{ cursor: 'pointer' }}
                          onClick={() => handleRowClick(user._id)}
                        >
                          <td className="text-uppercase">#{user.id || `CU-${user._id.slice(0, 6).padStart(3, '0')}`}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <img
                                src={user.picture || avatarSrc(user.name || user.full_name)}
                                alt={user.name || user.full_name || 'User'}
                                className="rounded-circle me-3"
                                style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                onError={(e) => { e.target.src = avatarSrc(user.name || user.full_name); }}
                              />
                              <span>{user.name || user.full_name || 'N/A'}</span>
                            </div>
                          </td>
                          <td>{user.email || 'N/A'}</td>
                          <td>{user.booking_count || 0}</td>
                          <td>{currency} {parseFloat(user.total_spent || 0).toFixed(2)}</td>
                          <td>{user.createdAt ? formatDate(user.createdAt, 'DD MMM, YYYY') : 'N/A'}</td>
                          <td>
                            <button 
                              className="p-0 btn btn-transparent border-0"
                              onClick={(e) => updateStatus(e, user._id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <span 
                                className={`badge rounded-pill px-3 py-2 fw-semibold border ${
                                  user.status 
                                    ? 'badge-light-success text-success border-success' 
                                    : 'badge-light-danger text-danger border-danger'
                                }`}
                                style={{ 
                                  fontSize: '0.9rem',
                                  minWidth: '80px',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                {user.status ? 'Active' : 'Restricted'}
                              </span>
                            </button>
                          </td>
                          <td className="text-end text-nowrap">
                            <button
                              className="btn btn-sm btn-icon btn-light-primary me-2"
                              title="Edit account"
                              onClick={(e) => openModal(e, setEditUser, user)}
                            >
                              <i className="bi bi-pencil-square fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-light-primary me-2"
                              title="Change password"
                              onClick={(e) => openModal(e, setPasswordUser, user)}
                            >
                              <i className="bi bi-key fs-6"></i>
                            </button>
                            <button
                              className="btn btn-sm btn-icon btn-light-danger"
                              title="Delete account"
                              onClick={(e) => deleteUser(e, user)}
                            >
                              <i className="bi bi-trash fs-6"></i>
                            </button>
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="8" className="text-dark text-center py-8">
                            {isLoader ? (
                              <Loader text="Loading" size="30" />
                            ) : (
                              "No data found!!"
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                  
                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <span className="text-muted">
                      Show {pagination?.per_page || 10} out of {pagination?.total_records || 0}
                    </span>
                    {pagination.total_pages > 1 && (
                      <Paginator
                        page={pagination.current_page}
                        rows={pagination.per_page}
                        totalRecords={pagination.total_records}
                        onPageChange={handlePageChange}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <EditUserModal
        show={!!editUser}
        user={editUser}
        onHide={() => setEditUser(null)}
        onSaved={getUsers}
      />
      <ChangePasswordModal
        show={!!passwordUser}
        user={passwordUser}
        onHide={() => setPasswordUser(null)}
      />
    </div>
  );
};

export default List;
